import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { providerOwnerApi } from "./provider-owner.api";
import type {
  ProviderOwnerStatusDto,
  SaveProviderDocumentsStepDto,
  SaveProviderMediaStepDto,
  SaveProviderCalendarStepDto,
  UpsertProviderMediaAssetDto,
  UpsertProviderPricingItemDto,
  UpsertProviderTeamMemberDto,
} from "./provider-owner.types";

type ProviderId = string;

type UpdateMediaAssetVariables = {
  mediaId: string;
  payload: UpsertProviderMediaAssetDto;
};

type UpdatePricingItemVariables = {
  pricingId: string;
  payload: UpsertProviderPricingItemDto;
};

type UpdateTeamMemberVariables = {
  teamMemberId: string;
  payload: UpsertProviderTeamMemberDto;
};

export const providerOwnerQueryKeys = {
  all: ["provider-owner"] as const,

  provider: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId] as const,

  dashboard: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "dashboard"] as const,

  onboardingStatus: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "onboarding-status"] as const,

  media: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "media"] as const,

  pricing: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "pricing"] as const,

  team: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "team"] as const,

  calendar: (providerId: ProviderId) =>
    ["provider-owner", "provider", providerId, "calendar"] as const,
};

const invalidateProviderOwnerScope = async (
  queryClient: QueryClient,
  providerId: ProviderId,
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.provider(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.dashboard(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.onboardingStatus(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.media(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.pricing(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.team(providerId),
    }),
    queryClient.invalidateQueries({
      queryKey: providerOwnerQueryKeys.calendar(providerId),
    }),
  ]);
};

export const useProviderOwnerDashboardSnapshot = (
  providerId: ProviderId,
) =>
  useQuery({
    queryKey: providerOwnerQueryKeys.dashboard(providerId),
    queryFn: () => providerOwnerApi.getDashboardSnapshot(providerId),
    enabled: false,
  });

/**
 * Temporarily disabled because the backend does not currently expose:
 *
 * /provider-owner/providers/:providerId/onboarding-status
 *
 * The onboarding stepper falls back to LEAD_CREATED while this endpoint
 * is not implemented.
 */
export const useProviderOwnerOnboardingStatus = (
  providerId: ProviderId,
) =>
  useQuery<ProviderOwnerStatusDto | undefined>({
    queryKey: providerOwnerQueryKeys.onboardingStatus(providerId),
    queryFn: async () => undefined,
    enabled: false,
  });

export const useProviderOwnerDashboard = (providerId: ProviderId) => {
  const snapshotQuery = useProviderOwnerDashboardSnapshot(providerId);
  const statusQuery = useProviderOwnerOnboardingStatus(providerId);

  return {
    snapshotQuery,
    statusQuery,
    isLoading: false,
    isError: false,
  };
};

export const canSubmitOnboardingForReview = (
  status: ProviderOwnerStatusDto,
) => {
  return (
    (
      status.status === "DRAFT_IN_PROGRESS" ||
      status.status === "AWAITING_PROVIDER_COMPLETION"
    ) &&
    status.pendingBlockers === 0
  );
};

export const useSubmitProviderOnboardingForReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (providerId: ProviderId) =>
      providerOwnerApi.submitOnboardingForReview(providerId),

    onSuccess: async (_, providerId) => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useProviderMediaAssets = (providerId: ProviderId) =>
  useQuery({
    queryKey: providerOwnerQueryKeys.media(providerId),
    queryFn: () => providerOwnerApi.getMediaAssets(providerId),
    enabled: Boolean(providerId),
  });

export const useCreateProviderMediaAsset = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertProviderMediaAssetDto) =>
      providerOwnerApi.createMediaAsset(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useUpdateProviderMediaAsset = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mediaId,
      payload,
    }: UpdateMediaAssetVariables) =>
      providerOwnerApi.updateMediaAsset(providerId, mediaId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useDeleteProviderMediaAsset = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mediaId: string) =>
      providerOwnerApi.deleteMediaAsset(providerId, mediaId),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useProviderPricingItems = (providerId: ProviderId) =>
  useQuery({
    queryKey: providerOwnerQueryKeys.pricing(providerId),
    queryFn: () => providerOwnerApi.getPricingItems(providerId),
    enabled: Boolean(providerId),
  });

export const useCreateProviderPricingItem = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertProviderPricingItemDto) =>
      providerOwnerApi.createPricingItem(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useUpdateProviderPricingItem = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pricingId,
      payload,
    }: UpdatePricingItemVariables) =>
      providerOwnerApi.updatePricingItem(providerId, pricingId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useDeleteProviderPricingItem = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pricingId: string) =>
      providerOwnerApi.deletePricingItem(providerId, pricingId),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useProviderTeamMembers = (providerId: ProviderId) =>
  useQuery({
    queryKey: providerOwnerQueryKeys.team(providerId),
    queryFn: () => providerOwnerApi.getTeamMembers(providerId),
    enabled: Boolean(providerId),
  });

export const useCreateProviderTeamMember = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertProviderTeamMemberDto) =>
      providerOwnerApi.createTeamMember(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useUpdateProviderTeamMember = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamMemberId,
      payload,
    }: UpdateTeamMemberVariables) =>
      providerOwnerApi.updateTeamMember(
        providerId,
        teamMemberId,
        payload,
      ),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useDeleteProviderTeamMember = (providerId: ProviderId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamMemberId: string) =>
      providerOwnerApi.deleteTeamMember(providerId, teamMemberId),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useSaveProviderDocumentsStep = (
  providerId: ProviderId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveProviderDocumentsStepDto) =>
      providerOwnerApi.saveDocumentsStep(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useSaveProviderMediaStep = (
  providerId: ProviderId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveProviderMediaStepDto) =>
      providerOwnerApi.saveMediaStep(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useSaveProviderPricingStep = (
  providerId: ProviderId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertProviderPricingItemDto) =>
      providerOwnerApi.savePricingStep(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useSaveProviderTeamStep = (
  providerId: ProviderId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertProviderTeamMemberDto) =>
      providerOwnerApi.saveTeamStep(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};

export const useProviderCalendar = (providerId: ProviderId) =>
  useQuery({
    queryKey: providerOwnerQueryKeys.calendar(providerId),
    queryFn: () => providerOwnerApi.getCalendar(providerId),
    enabled: Boolean(providerId),
  });

export const useSaveProviderCalendarStep = (
  providerId: ProviderId,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveProviderCalendarStepDto) =>
      providerOwnerApi.saveCalendarStep(providerId, payload),

    onSuccess: async () => {
      await invalidateProviderOwnerScope(queryClient, providerId);
    },
  });
};